/**
 * @graphql cc
 */

import gql from 'graphql-tag';
import React, { useEffect, useMemo, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import { defineMessages, useIntl } from 'react-intl-next';

import {
	updateCommentsDataState,
	useCommentsData,
	useResolvedCommentsQuery,
} from '@confluence/comments-data';
import { usePageContentId } from '@confluence/page-context';
import { useUnreadComments } from '@confluence/unread-comments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { useSessionData } from '@confluence/session-data';
import { useCommentService } from '@confluence/content-types-bridge-comments';
import { useEditorAnnotations } from '@confluence/inline-comments-hooks';
import type { FlagsStateContainer } from '@confluence/flags';
import { fg } from '@confluence/feature-gating';
import { useRendererActions } from '@confluence/renderer-actions';
import {
	getMarkIds,
	useCommentsPanel,
	ViewValues,
	getResolvedCommentThreads,
	getVisibleCommentThreads,
	useCommentsPanelScroll,
	LocationValue,
} from '@confluence/comments-panel-utils';
import { SIDEBAR_COMMENTS_PANEL_LOAD_RESOLVED_EXPERIENCE } from '@confluence/experience-tracker';

import { CommentsPanelList } from '../components/CommentsPanelList';
import { Loading } from '../components/Loading';
import { ErrorPanel } from '../components/ErrorPanel';
import { EmptyResolvedSVG } from '../assets/EmptyResolvedSVG';
import { EmptyStateComponent } from '../components/EmptyStateComponent';
import { onlyHasReactionsSummaryErrors } from '../helper/commentHelpers';
import { useCommentsPanelMetrics } from '../hooks/useCommentsPanelMetrics';
import { COMMENTS_PANEL_RESOLVED_VIEW_METRIC } from '../perf.config';

import { GraphQLContentStatus } from './__types__/ResolvedViewResolvedInlineCommentsQuery';
import type {
	ResolvedViewResolvedInlineCommentsQuery as ResolvedViewResolvedInlineCommentsQuery$data,
	ResolvedViewResolvedInlineCommentsQueryVariables as ResolvedViewResolvedInlineCommentsQuery$variables,
} from './__types__/ResolvedViewResolvedInlineCommentsQuery';
import { RESOLVED_TOP_LEVEL_ACTIONS } from './topLevelActions';

const ResolvedViewResolvedInlineCommentsQuery = gql`
	query ResolvedViewResolvedInlineCommentsQuery(
		$pageId: ID!
		$contentStatus: [GraphQLContentStatus!]
	) {
		comments(
			pageId: $pageId
			contentStatus: $contentStatus
			confluenceCommentFilter: { commentState: RESOLVED, commentType: INLINE }
		) @allowHigherTimeout {
			nodes {
				id
				body(representation: ATLAS_DOC_FORMAT) {
					value
				}
				contentStatus
				createdAt {
					value
				}
				createdAtNonLocalized
				links {
					webui
					editui
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							resolved
							isDangling
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
					... on FooterComment {
						commentResolveProperties {
							resolved
							resolvedFriendlyDate
							resolvedTime
							resolvedUser
						}
					}
				}
				parentId
				permissions {
					isEditable
					isRemovable
					isResolvable
				}
				version {
					by {
						displayName
					}
					when
					number
				}
				author {
					displayName
					profilePicture {
						path
					}
					permissionType
					... on KnownUser {
						accountId
					}
				}
				location {
					type
					... on InlineComment {
						inlineMarkerRef
						inlineText
						inlineResolveProperties {
							isDangling
							resolvedTime
							resolvedFriendlyDate
							resolvedUser
							resolved
						}
					}
				}
				createdAt {
					value
				}
				createdAtNonLocalized
				replies {
					id
					body(representation: ATLAS_DOC_FORMAT) {
						value
					}
					contentStatus
					createdAt {
						value
					}
					createdAtNonLocalized
					links {
						webui
						editui
					}
					location {
						type
						... on InlineComment {
							inlineMarkerRef
							inlineText
							inlineResolveProperties {
								resolved
								isDangling
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
						... on FooterComment {
							commentResolveProperties {
								resolved
								resolvedFriendlyDate
								resolvedTime
								resolvedUser
							}
						}
					}
					parentId
					permissions {
						isEditable
						isRemovable
						isResolvable
					}
					version {
						by {
							displayName
						}
						when
						number
					}
					author {
						displayName
						profilePicture {
							path
						}
						permissionType
						... on KnownUser {
							accountId
						}
					}
					createdAt {
						value
					}
					createdAtNonLocalized
				}
			}
			totalCount
		}
	}
`;
type ResolvedViewProps = {
	flags?: FlagsStateContainer;
};

const i18n = defineMessages({
	emptyCommentsHeaderText: {
		id: 'comments-panel.resolved.view.empty.header.text',
		defaultMessage: 'No resolved comments',
		description:
			'Header text to display in Resolved View empty state when there are no resolved comments on the page.',
	},
	emptyCommentsBodyText: {
		id: 'comments-panel.resolved.view.empty.text',
		defaultMessage:
			'Got comments that aren’t needed? Resolve them! They’ll display here, so you can reopen and add them back in the future.',
		description:
			'Body text to display in Resolved View empty state when there are no resolved comments on the page.',
	},
});

export const ResolvedView = ({ flags }: ResolvedViewProps) => {
	const [loading, setLoading] = useState(true);
	const { formatMessage } = useIntl();

	const [contentId] = usePageContentId();

	const [{ commentsDataMap }, { addNewCommentThreads, setCommentsDataMap, clearRemovedComments }] =
		useCommentsData();
	const [{ readCommentsListState }] = useUnreadComments();
	const [{ annotations }] = useEditorAnnotations();
	const { rendererActions } = useRendererActions();
	const pageMode = useGetPageMode();
	const { userId: currentUserId } = useSessionData();
	const [
		{
			initialDataLoadedForViewMap,
			showInlineComments,
			showGeneralComments,
			currentlySelectedThreadKey,
			commentType,
			currentView,
			commentContainerToFocus,
		},
		{ setInitialDataLoadedForView },
	] = useCommentsPanel();
	const isInitialCommentDataLoaded = initialDataLoadedForViewMap[ViewValues.RESOLVED];
	const { commentService } = useCommentService();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();
	const [allCommentsRendered, setAllCommentsRendered] = useState(false);
	const [commentBatchRendered, setCommentBatchRendered] = useState(false);

	const annotationsInEditorDoc = useMemo(() => {
		return new Set(annotations);
	}, [annotations]);

	/* eslint-disable react-hooks/rules-of-hooks */
	const {
		loading: queryLoading,
		error,
		refetch,
		data,
	} = fg('confluence_frontend_comments_panel_v2')
		? useResolvedCommentsQuery({ pageId: contentId || '' })
		: useQuery<
				ResolvedViewResolvedInlineCommentsQuery$data,
				ResolvedViewResolvedInlineCommentsQuery$variables
			>(ResolvedViewResolvedInlineCommentsQuery, {
				variables: {
					pageId: contentId ?? '',
					contentStatus: [
						GraphQLContentStatus.DRAFT,
						GraphQLContentStatus.CURRENT,
					] satisfies GraphQLContentStatus[],
				},
				onCompleted: (data: ResolvedViewResolvedInlineCommentsQuery$data) => {
					const hasAlreadyFetchedInitialData = Object.keys(commentsDataMap).length > 0;
					updateCommentsDataState({
						data: data as any, // Just to coerce this type
						readCommentsListState,
						updateData: hasAlreadyFetchedInitialData ? addNewCommentThreads : setCommentsDataMap,
						annotationsInEditorDoc,
						pageMode,
						currentUserId: currentUserId ?? '',
						isOpen: false,
					});
					setLoading(false);
				},
				onError: fg('confluence_frontend_handle_reactions_errors')
					? undefined
					: () => {
							setLoading(false);
						},
			});
	/* eslint-enable react-hooks/rules-of-hooks */

	useCommentsPanelMetrics({
		loading,
		error,
		allCommentsRendered,
		commentBatchRendered,
		totalComments: data?.comments?.nodes?.length,
		contentId,
		pageMode,
		experienceName: SIDEBAR_COMMENTS_PANEL_LOAD_RESOLVED_EXPERIENCE,
		metric: COMMENTS_PANEL_RESOLVED_VIEW_METRIC,
	});

	useEffect(() => {
		if (error && fg('confluence_frontend_handle_reactions_errors')) {
			setLoading(false);
		}
	}, [error]);

	useEffect(() => {
		if (!fg('confluence_frontend_comments_panel_v2')) {
			if (queryLoading) {
				setLoading(true);
			}
		} else {
			setLoading(queryLoading);
		}
	}, [queryLoading, setLoading]);

	useEffect(() => {
		if (!loading && !isInitialCommentDataLoaded) {
			setInitialDataLoadedForView({ viewToSetLoaded: ViewValues.RESOLVED });

			// handle focused comment
			if (currentlySelectedThreadKey) {
				scrollCommentsPanelToThread({
					annotationId: currentlySelectedThreadKey,
					commentType,
					isCommentThreadResolved: true,
					scrollOnLoad: true,
					commentContainerToFocus,
				});
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps -- ignore change in currentlySelectedThreadKey since we only wan to scroll the first time for focused comments
	}, [loading, setInitialDataLoadedForView]);

	useEffect(() => {
		// When this view unmounts, we want to clear out the "resolved"/"deleted" comments
		return () => {
			clearRemovedComments();
		};
	}, [clearRemovedComments]);

	// When a comment is made in content types, we want to trigger a refresh of the comments panel
	useEffect(() => {
		commentService.registerTriggerCommentPanelRefresh(refetch);
		return () => {
			// un-set the handler on component unmount
			commentService.registerTriggerCommentPanelRefresh(undefined);
		};
	}, [commentService, refetch]);

	const resolvedThreads = useMemo(() => {
		if (!isInitialCommentDataLoaded && loading) {
			return [];
		}

		const orderedAnnotationIds =
			pageMode === PageMode.VIEW ? getMarkIds(rendererActions?.getAnnotationMarks()) : annotations;

		return fg('confluence_frontend_immediate_comment_removal')
			? getVisibleCommentThreads({
					currentView,
					commentsDataMap,
					includeGeneralComments: showGeneralComments,
					includeInlineComments: showInlineComments,
					orderedAnnotationIds,
				})
			: getResolvedCommentThreads({
					commentsDataMap,
					showInlineComments,
					showGeneralComments,
					location: LocationValue.PANEL,
				});
	}, [
		annotations,
		commentsDataMap,
		currentView,
		isInitialCommentDataLoaded,
		loading,
		pageMode,
		rendererActions,
		showGeneralComments,
		showInlineComments,
	]);

	if (!isInitialCommentDataLoaded) {
		if (loading) {
			return <Loading />;
		}

		if (
			error &&
			(!onlyHasReactionsSummaryErrors(error) || !fg('confluence_frontend_handle_reactions_errors'))
		) {
			return (
				<ErrorPanel
					error={error}
					onRetryClick={async () => {
						await refetch();
					}}
				/>
			);
		}
	}

	if (resolvedThreads.length === 0) {
		return (
			<EmptyStateComponent
				SVG={EmptyResolvedSVG}
				emptyCommentsHeaderText={formatMessage(i18n.emptyCommentsHeaderText)}
				emptyCommentsBodyText={formatMessage(i18n.emptyCommentsBodyText)}
			/>
		);
	}

	return (
		<CommentsPanelList
			commentThreads={resolvedThreads}
			supportedTopLevelActions={RESOLVED_TOP_LEVEL_ACTIONS}
			flags={flags}
			onAllCommentsRendered={() => setAllCommentsRendered(true)}
			onCommentBatchRendered={() => setCommentBatchRendered(true)}
		/>
	);
};
